import { useEffect, useState } from "react";
import { useParams } from "react-router";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { backendBase } from "@/api/backendBase";

interface StatusStat {
  status: string;
  total: number;
  matched: number;
  matchPct: number;
}

interface ConfusionPair {
  expected: string;
  computed: string;
  count: number;
}

interface RuleStat {
  ruleId: string;
  total: number;
  matched: number;
  matchPct: number;
}

interface AnalyseReportResponse {
  date: string;
  reportFile: string;
  sourceRowCounts: Record<string, number>;
  overall: { compared: number; matched: number; mismatched: number; mismatchPct: number };
  excludingCompleted: {
    status: string;
    compared: number;
    matched: number;
    mismatched: number;
    mismatchPct: number;
  };
  byExpectedStatus: StatusStat[];
  confusionPairs: ConfusionPair[];
  byRule: RuleStat[];
  findings: string[];
}

type MuiColor = "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";

const pctColor = (pct: number): MuiColor => {
  if (pct >= 90) return "success";
  if (pct >= 50) return "warning";
  return "error";
};

export const ReportAnalysis = () => {
  const params = useParams();
  const date = params.date;

  const [data, setData] = useState<AnalyseReportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!date) return;
    setData(null);
    setError(null);
    fetch(`${backendBase()}/sandbox/analyse-report?date=${date}`)
      .then((res) => res.json())
      .then((json) => {
        if (json.error) {
          setError(json.error);
          return;
        }
        setData(json);
      })
      .catch((e) => setError(String(e)));
  }, [date]);

  if (!date) {
    return <Alert severity="error">Missing date in URL — expected /sandbox/report/analysis/&lt;date&gt;</Alert>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        maxWidth: 1200,
        mx: "auto",
      }}
    >
      <Typography variant="h4" gutterBottom>
        Status Rules Analysis — {data.date}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Report file: {data.reportFile}
      </Typography>

      <Stack
        direction="row"
        spacing={3}
        sx={{ mb: 4 }}
      >
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography
            variant="overline"
            color="text.secondary"
          >
            Overall
          </Typography>
          <Typography variant="h5">
            {data.overall.matched} / {data.overall.compared} matched
          </Typography>
          <Chip
            label={`${data.overall.mismatchPct}% mismatch`}
            color={pctColor(100 - data.overall.mismatchPct)}
            size="small"
            sx={{ mt: 1 }}
          />
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography
            variant="overline"
            color="text.secondary"
          >
            Excluding "{data.excludingCompleted.status}"
          </Typography>
          <Typography variant="h5">
            {data.excludingCompleted.matched} / {data.excludingCompleted.compared} matched
          </Typography>
          <Chip
            label={`${data.excludingCompleted.mismatchPct}% mismatch`}
            color={pctColor(100 - data.excludingCompleted.mismatchPct)}
            size="small"
            sx={{ mt: 1 }}
          />
        </Paper>
      </Stack>

      <Typography
        variant="h6"
        gutterBottom
      >
        Findings
      </Typography>
      <Stack
        spacing={1}
        sx={{ mb: 4 }}
      >
        {data.findings.map((finding, i) => (
          <Alert
            key={i}
            severity="warning"
            variant="outlined"
          >
            {finding}
          </Alert>
        ))}
      </Stack>

      <Typography
        variant="h6"
        gutterBottom
      >
        By Expected Status
      </Typography>
      <TableContainer
        component={Paper}
        sx={{ mb: 4 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Expected Status</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Matched</TableCell>
              <TableCell align="right">%</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.byExpectedStatus.map((s) => (
              <TableRow key={s.status}>
                <TableCell>{s.status}</TableCell>
                <TableCell align="right">{s.total}</TableCell>
                <TableCell align="right">{s.matched}</TableCell>
                <TableCell align="right">
                  <Chip
                    label={`${s.matchPct}%`}
                    color={pctColor(s.matchPct)}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography
        variant="h6"
        gutterBottom
      >
        Top Confusion Pairs
      </Typography>
      <TableContainer
        component={Paper}
        sx={{ mb: 4 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="right">Count</TableCell>
              <TableCell>Expected</TableCell>
              <TableCell>Computed instead</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.confusionPairs.map((c, i) => (
              <TableRow key={i}>
                <TableCell align="right">{c.count}</TableCell>
                <TableCell>{c.expected}</TableCell>
                <TableCell>{c.computed}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography
        variant="h6"
        gutterBottom
      >
        By Rule
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Rule</TableCell>
              <TableCell align="right">Fires</TableCell>
              <TableCell align="right">Correct</TableCell>
              <TableCell align="right">%</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.byRule.map((r) => (
              <TableRow key={r.ruleId}>
                <TableCell>{r.ruleId}</TableCell>
                <TableCell align="right">{r.total}</TableCell>
                <TableCell align="right">{r.matched}</TableCell>
                <TableCell align="right">
                  <Chip
                    label={`${r.matchPct}%`}
                    color={pctColor(r.matchPct)}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
